import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Minisite from '../../models/minisite';
import WidgetEvent from '../../models/widgetEvent';

import { Button, Tooltip, SplitContainer, FrameCard, TooltipHelp, PreviewCard, LinkWithActions } from '@weezevent/nacre';
import { useTrackingPage, useSegment } from '../../components/tracking/trackingProvider';
import EventCreationStepSideBar from './eventCreationStepSideBar';
import { Link, useHistory } from 'react-router-dom';
import ModalIntegrationCode from '../widgets/modal_integration_code/modalIntegrationCode';
import StylesPostCreation from '../../styles/eventCreation/eventPostCreation.module.css';
import Styles from '../../styles/eventCreation/eventCreation.module.css';

export function ShareSocialNetwork({ label = 'Share', url = '' }) {
    const { track } = useSegment();
    return (
        <div className={StylesPostCreation['social-container']}>
            <span className={StylesPostCreation['social-title']}>{label}</span>
            <div className={StylesPostCreation['social-icons']}>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.facebook.com/sharer?u=${url}`}
                    onClick={() => {
                        track('ACTION > CLICK ICON FACEBOOK', { 'Page Title': 'CREATE EVENT' });
                    }}
                >
                    <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <title>Facebook</title>
                        <path d="M23.9981 11.9991C23.9981 5.37216 18.626 0 11.9991 0C5.37216 0 0 5.37216 0 11.9991C0 17.9882 4.38789 22.9522 10.1242 23.8524V15.4676H7.07758V11.9991H10.1242V9.35553C10.1242 6.34826 11.9156 4.68714 14.6564 4.68714C15.9692 4.68714 17.3424 4.92149 17.3424 4.92149V7.87439H15.8294C14.3388 7.87439 13.8739 8.79933 13.8739 9.74824V11.9991H17.2018L16.6698 15.4676H13.8739V23.8524C19.6103 22.9522 23.9981 17.9882 23.9981 11.9991Z" />
                    </svg>
                </a>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://twitter.com/intent/tweet?text=${url}`}
                    onClick={() => {
                        track('ACTION > CLICK ICON TWITTER', { 'Page Title': 'CREATE EVENT' });
                    }}
                >
                    <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <title>Twitter</title>
                        <path d="M23.954 4.569c-.885.389-1.83.654-2.825.775 1.014-.611 1.794-1.574 2.163-2.723-.951.555-2.005.959-3.127 1.184-.896-.959-2.173-1.559-3.591-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124C7.691 8.094 4.066 6.13 1.64 3.161c-.427.722-.666 1.561-.666 2.475 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.228-.616v.061c0 2.385 1.693 4.374 3.946 4.827-.413.111-.849.171-1.296.171-.314 0-.615-.03-.916-.086.631 1.953 2.445 3.377 4.604 3.417-1.68 1.319-3.809 2.105-6.102 2.105-.39 0-.779-.023-1.17-.067 2.189 1.394 4.768 2.209 7.557 2.209 9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63.961-.689 1.8-1.56 2.46-2.548l-.047-.02z" />
                    </svg>
                </a>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
                    onClick={() => {
                        track('ACTION > CLICK ICON LINKEDIN', { 'Page Title': 'CREATE EVENT' });
                    }}
                >
                    <svg role="img" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <title>Linkedin</title>
                        <path d="m437 0h-362c-41.351562 0-75 33.648438-75 75v362c0 41.351562 33.648438 75 75 75h362c41.351562 0 75-33.648438 75-75v-362c0-41.351562-33.648438-75-75-75zm-256 406h-60v-210h60zm0-240h-60v-60h60zm210 240h-60v-120c0-16.539062-13.460938-30-30-30s-30 13.460938-30 30v120h-60v-210h60v11.308594c15.71875-4.886719 25.929688-11.308594 45-11.308594 40.691406.042969 75 36.546875 75 79.6875zm0 0" />
                    </svg>
                </a>
            </div>
        </div>
    );
}

ShareSocialNetwork.propTypes = {
    label: PropTypes.node,
    url: PropTypes.string
};

function OptionLink({ label = '', labelTootip = '', url = '', onClick = () => {} }) {
    return (
        <div className={StylesPostCreation['options_card_list_option']} onClick={onClick}>
            <span>
                <Link target="_blank" className={StylesPostCreation['options_card_list_option']} to={url}>
                    {label}
                </Link>
                <TooltipHelp title={labelTootip} position={'top'} mini={false} />
            </span>
        </div>
    );
}

OptionLink.propTypes = {
    label: PropTypes.node,
    labelTootip: PropTypes.node,
    url: PropTypes.string,
    onClick: PropTypes.func
};

export function MainInfos({ organization, event, minisite = null }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const history = useHistory();

    return (
        <FrameCard title={t('ticket.creationEvent.recap.titlePublished')}>
            <div className={StylesPostCreation['main_card_title']}>{t('ticket.creationEvent.recap.mainText')}</div>
            <Button
                primary
                className={minisite?.is_published ? StylesPostCreation['main_card_dashboard_button'] : ''}
                size="regular"
                onClick={() => {
                    track('ACTION > ACCESS HOME', { 'Page Title': 'CREATE EVENT' });
                    history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                }}
            >
                {t('ticket.creationEvent.recap.buttonDashboard')}
            </Button>
            {minisite?.is_published && <ShareSocialNetwork label={t('ticket.creationEvent.recap.shareEvent')} url={minisite.url} />}
        </FrameCard>
    );
}

MainInfos.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    minisite: PropTypes.object
};

export function NextActions({ organization, event }) {
    const { t } = useTranslation();
    const { track } = useSegment();

    return (
        <FrameCard className={StylesPostCreation['options_card']} title={t('ticket.creationEvent.recap.titleOptions')} subtitle={t('ticket.creationEvent.recap.subtitleOptions')}>
            <OptionLink
                label={t('ticket.creationEvent.recap.dataEvents')}
                labelTootip={t('ticket.creationEvent.recap.tooltipOption.dataEvents')}
                url={`/ticket/O${organization.id}/E${event.id}/settings/event`}
                onClick={() => track('ACTION > SHORTCUT EVENT DATA', { 'Page Title': 'CREATE EVENT' })}
            />
            <OptionLink
                label={t('ticket.creationEvent.recap.addRates')}
                labelTootip={t('ticket.creationEvent.recap.tooltipOption.addRates')}
                url={`/ticket/O${organization.id}/E${event.id}/settings/rates`}
                onClick={() => track('ACTION > SHORTCUT ADD RATES', { 'Page Title': 'CREATE EVENT' })}
            />
            <OptionLink
                label={t('ticket.creationEvent.recap.addForm')}
                labelTootip={t('ticket.creationEvent.recap.tooltipOption.addForm')}
                url={`/ticket/O${organization.id}/E${event.id}/settings/forms`}
                onClick={() => track('ACTION > SHORTCUT FORM', { 'Page Title': 'CREATE EVENT' })}
            />
            <OptionLink
                label={t('ticket.creationEvent.recap.customizeTickets')}
                labelTootip={t('ticket.creationEvent.recap.tooltipOption.customizeTickets')}
                url={`/ticket/O${organization.id}/E${event.id}/settings/designs`}
                onClick={() => track('ACTION > SHORTCUT CUSTOM', { 'Page Title': 'CREATE EVENT' })}
            />
        </FrameCard>
    );
}

NextActions.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};

export function IntegrationWidget({ event }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [showModal, setShowModal] = React.useState(false);

    const widget = new WidgetEvent({
        widget_event_key: `E${event.id}`,
        event
    });

    return (
        <FrameCard title={t('ticket.creationEvent.recap.titleWidget')} subtitle={t('ticket.creationEvent.recap.subtitleWidget')}>
            {showModal && <ModalIntegrationCode widget={widget} code={showModal} displayCode={setShowModal} />}
            <Tooltip triggerOnHover title={t('ticket.creationEvent.recap.tooltipIntegrationCode')} margin={false}>
                <Button
                    size={'regular'}
                    onClick={() => {
                        track('ACTION > ACCESS MODULE', { 'Page Title': 'CREATE EVENT' });
                        setShowModal(true);
                    }}
                >
                    {t('ticket.creationEvent.recap.buttonIntegrationCode')}
                </Button>
            </Tooltip>
        </FrameCard>
    );
}

IntegrationWidget.propTypes = {
    event: PropTypes.object.isRequired
};

export function PreviewMinisite({ url }) {
    return (
        <div className={StylesPostCreation['wrap-iframe']}>
            <div className={StylesPostCreation['transparent']} onClick={e => e.preventDefault()} />
            <iframe
                scrolling="no"
                title={'preview_minisite'}
                sandbox={'allow-same-origin allow-forms allow-popups allow-scripts allow-top-navigation allow-pointer-lock allow-top-navigation-by-user-activation'}
                className={StylesPostCreation['iframe']}
                src={url}
            />
        </div>
    );
}

PreviewMinisite.propTypes = {
    url: PropTypes.string.isRequired
};

export function MinisiteCard({ organization, event, minisite = null, loading = false }) {
    const { t } = useTranslation();
    const { track } = useSegment();

    if (loading || !minisite) {
        return true;
    }

    return (
        <PreviewCard title={t('ticket.creationEvent.recap.labelPreviewMinisite')}>
            <PreviewMinisite url={minisite.preview_url} />
            <div className={StylesPostCreation['preview-card__container-link']}>
                {minisite.is_published && (
                    <div onClick={() => track('ACTION > URL MINISITE', { 'Page Title': 'CREATE EVENT' })}>
                        <LinkWithActions
                            tooltipText={t('ticket.creationEvent.recap.tooltipLink')}
                            tooltipCopy={<div className={StylesPostCreation['tooltip-copy__content']}>{t('ticket.creationEvent.recap.tooltipClipboard')}</div>}
                            tooltipCopied={<div className={StylesPostCreation['tooltip-copy__content']}>{t('ticket.common.copied')}</div>}
                            url={minisite.url}
                        />
                    </div>
                )}
                <div className={StylesPostCreation['main_card_custom_minisite_button']} onClick={() => track('ACTION > CUSTOMIZE MINISITE', { 'Page Title': 'CREATE EVENT' })}>
                    <Link
                        className={'wz-framebutton wz-button wz-button-secondary'}
                        to={`/ticket/O${organization.id}/E${event.id}/channels/page`}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                    >
                        {t('ticket.creationEvent.recap.buttonCustomMinisite')}
                    </Link>
                </div>
            </div>
        </PreviewCard>
    );
}

MinisiteCard.propTypes = {
    event: PropTypes.object.isRequired,
    minisite: PropTypes.object,
    organization: PropTypes.object.isRequired,
    loading: PropTypes.bool
};

export default function EventPostCreationDev({ organization, organizationInfos, event }) {
    const { t } = useTranslation();

    const [minisite, isLoadingMinisite] = Minisite.useApiModel(
        {
            launch: Boolean(event.site?.toString()),
            cache: false,
            organizationId: organization.id,
            id: event.site
        },
        [event.site, organization]
    );

    const stepLabels = {
        1: t('ticket.creationEvent.stepSideBar.stepInfos'),
        2: t('ticket.creationEvent.stepSideBar.stepLocalization'),
        3: t('ticket.creationEvent.stepSideBar.stepDates'),
        4: t('ticket.creationEvent.stepSideBar.stepRates')
    };

    const steps = {
        1: 'done',
        2: 'done',
        3: 'done',
        4: 'done'
    };

    useTrackingPage('VIEW - POST CREATE EVENT', {
        'Page Title': 'POST CREATE EVENT'
    });

    return (
        <>
            <div className={Styles['event-creation-flex']}>
                <EventCreationStepSideBar steps={steps} labels={stepLabels} organization={organization} organizationInfos={organizationInfos} />
                <div className={Styles['event-creation-container']}>
                    <SplitContainer size={'maxi'} additional={<MinisiteCard event={event} organization={organization} minisite={minisite} loading={isLoadingMinisite} />}>
                        <MainInfos organization={organization} event={event} minisite={minisite} />
                        <NextActions organization={organization} event={event} />
                        <IntegrationWidget event={event} />
                    </SplitContainer>
                </div>
            </div>
        </>
    );
}

EventPostCreationDev.propTypes = {
    organization: PropTypes.object.isRequired,
    organizationInfos: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
